import { useCallback } from 'react';
import { useService } from '../ServiceContext';
import { useNotification } from './useNotification';
import { PropertyField } from '../../system/field';

interface UseSellingAgentEmailOptions {
  listingKey: string;
  listingId: string;
  mlsStatus: string;
  isListingOE: boolean;
  sellingAgentMlsId: string;
  coSellingAgentMlsId: string;
}

const ACCEPTED_STATUSES = [
  'ACTIVE UNDER CONTRACT-BRIGHT',
  'PENDING-BRIGHT',
  'CLOSED-BRIGHT',
];

export const useSellingAgentEmail = (options: UseSellingAgentEmailOptions) => {
  const {
    listingKey,
    listingId,
    mlsStatus,
    isListingOE,
    sellingAgentMlsId,
    coSellingAgentMlsId,
  } = options;

  const { getLiveValues } = useService();
  const { sendSellingAgentEmail } = useNotification();

  const getLiveListingValues = useCallback(async () => {
    try {
      if (!listingKey) {
        return;
      }
      return await getLiveValues(listingKey, true, [
        PropertyField.BUYER_AGENT_MLS_ID,
        PropertyField.CO_BUYER_AGENT_MLS_ID,
      ]);
    } catch (error) {
      console.error('Error getting live listing values', error);
      throw error;
    }
  }, [listingKey, getLiveValues]);

  const maybeSendSellingAgentEmail = useCallback(async () => {
    // Get current listing values before publishing over them
    // We're checking if one or more of the selling agents has changed,
    // to decide if we should send the selling agent email notification
    const liveValues = await getLiveListingValues();

    const isSellingAgentUpdated =
      sellingAgentMlsId.length > 0 &&
      sellingAgentMlsId !== liveValues?.Property?.BUYER_AGENT_MLS_ID &&
      sellingAgentMlsId !== liveValues?.Property?.CO_BUYER_AGENT_MLS_ID;

    const isCoSellingAgentUpdated =
      coSellingAgentMlsId.length > 0 &&
      coSellingAgentMlsId !== liveValues?.Property?.BUYER_AGENT_MLS_ID &&
      coSellingAgentMlsId !== liveValues?.Property?.CO_BUYER_AGENT_MLS_ID;

    const agentHasChanged = isSellingAgentUpdated || isCoSellingAgentUpdated;

    // Set up Buyer and/or CoBuyer mlsIds as email recipients
    const mlsIds: string[] = [];
    if (isSellingAgentUpdated && sellingAgentMlsId) {
      mlsIds.push(sellingAgentMlsId);
    }
    if (
      isCoSellingAgentUpdated &&
      coSellingAgentMlsId &&
      sellingAgentMlsId !== coSellingAgentMlsId
    ) {
      mlsIds.push(coSellingAgentMlsId);
    }

    // Conditions for sending the Selling Agent Email:
    // * Listing is Not OE
    // * Listing has status "Active Under Contract", "Pending", or "Closed"
    // * Buyer and/or Co-Buyer Agent are present on the listing
    // ** AND Buyer and/or Co-Buyer Agent has changed since last publish
    const shouldSendSellingAgentEmail =
      !isListingOE &&
      ACCEPTED_STATUSES.includes(mlsStatus) &&
      (sellingAgentMlsId || coSellingAgentMlsId) &&
      agentHasChanged;

    if (shouldSendSellingAgentEmail) {
      sendSellingAgentEmail(listingId, mlsIds);
    }
  }, [
    getLiveListingValues,
    sellingAgentMlsId,
    coSellingAgentMlsId,
    isListingOE,
    mlsStatus,
    listingId,
    sendSellingAgentEmail,
  ]);

  return { maybeSendSellingAgentEmail };
};
